import { useEffect, useState } from 'react';

// Below this many currently-waiting watchers, the count reads as noise
// rather than social proof — render nothing.
const MIN_COUNT_TO_SHOW = 2;

// Above this many, show "10+" rather than an exact (and quickly stale)
// number — matches the copy Bill approved. Must stay one less than
// RESPONSE_COUNT_CAP in src/pages/api/master/demand.ts (11) — the API caps
// its response there so a direct caller can't read past this display cap
// either. Move both together.
const DISPLAY_CAP = 10;

interface DemandApiResponse {
  count: number | null;
}

interface DemandBadgeProps {
  masterId: string;
}

/**
 * Social-proof badge shown in the /m/ empty state when a model has watchers
 * but no active listings — "N buyers are waiting for this model." Renders
 * nothing while loading, on fetch error, or when the currently-waiting
 * count is below MIN_COUNT_TO_SHOW.
 */
export default function DemandBadge({ masterId }: DemandBadgeProps) {
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Reset on every masterId change — without this, client-side /m/A -> /m/B
    // navigation would show A's stale count (or a stale fetchError from A
    // permanently suppressing every later model) until B's fetch resolves.
    setCount(null);
    setLoading(true);
    setFetchError(false);

    async function fetchDemand() {
      try {
        const res = await fetch(`/api/master/demand?masterId=${encodeURIComponent(masterId)}`);
        if (!res.ok) throw new Error('API error');
        const json: DemandApiResponse = await res.json();
        if (cancelled) return;
        // Reject non-integers / negatives (NaN, Infinity, floats) — the API
        // contract is a non-negative integer or null; anything else is
        // treated the same as a fetch failure rather than rendered as-is.
        if (Number.isInteger(json.count) && (json.count as number) >= 0) {
          setCount(json.count as number);
        } else {
          setFetchError(true);
        }
      } catch {
        if (!cancelled) setFetchError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchDemand();
    return () => {
      cancelled = true;
    };
  }, [masterId]);

  if (loading || fetchError || count === null || count < MIN_COUNT_TO_SHOW) {
    return null;
  }

  const displayCount = count > DISPLAY_CAP ? `${DISPLAY_CAP}+` : count;

  // Brand orange via the `primary` token (Bill, Aug 2026: one orange site-wide,
  // #C75008). This landed on main as a hardcoded legacy #EF9021 while the
  // redesign was in flight.
  return (
    <div className="rounded-[1.2rem] border border-primary/30 bg-primary/10 px-[1.6rem] py-[1.2rem] text-center">
      <p className="text-[1.5rem] font-bold text-[#242E5F] leading-[1.3]">
        {displayCount} buyers are waiting for this model
      </p>
      <p className="text-[1.3rem] text-brand-dark-gray leading-[1.4] mt-[0.2rem]">
        List yours and they&apos;ll be notified automatically.
      </p>
    </div>
  );
}
